import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useGetLearningPathLessons from "../../hooks/api/learning-path/useGetLearningPathLessons";
import useDeleteLesson from "../../hooks/api/lesson/useDeleteLesson";
import { LessonSummary } from "../../models/Lesson";
import GenericTable from "./GenericTable";

type Props = {
  learningPathId: string;
};

export default function LessonsTable({ learningPathId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: lessons,
    isPending,
    isError,
    mutateAsync: fetchLessons,
    variables,
  } = useGetLearningPathLessons(learningPathId);
  const { mutateAsync: deleteLesson, isPending: isDeletionPending } =
    useDeleteLesson();

  const onDeleteConfirm = useCallback(
    (lesson: LessonSummary) => {
      deleteLesson({ lessonId: lesson.id! }).finally(() =>
        fetchLessons({ page: variables?.page ?? 0 }),
      );
    },
    [deleteLesson, fetchLessons, variables?.page],
  );

  return (
    <GenericTable<LessonSummary>
      items={lessons?.content ?? []}
      headers={[t("forms.name"), t("forms.description")]}
      rowExtractors={[(lesson) => lesson.name, (lesson) => lesson.description]}
      onEditClick={(lesson) => navigate(`./lessons/${lesson.id}`)}
      onDeleteConfirm={onDeleteConfirm}
      isDeletionPending={isDeletionPending}
      isLoading={isPending}
      isError={isError}
      refetch={fetchLessons}
      pagination={lessons?.page}
    />
  );
}
